// University of California, Riverside: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UC Riverside's Common Data Set 2025-2026 (ir.ucr.edu, posted May 2026):
//    C1 applicants/admits/enrollees for Fall 2025 with the residency table,
//    C8 (no use of SAT or ACT), C21/C22 (no early decision, no early action).
//    Its section G leaves 2026-2027 costs blank ("Varies according to cohort
//    year"), so costs come from the financial aid office instead.
//  - admissions.ucr.edu First Year page: the "Test Free" statement and the
//    first-year dates and deadlines.
//  - financialaid.ucr.edu cost page: the 2026-2027 budget for new first-year
//    students, in university housing, California resident and out-of-state
//    tables. Quirk: the out-of-state table prints a $85,809 total, but its
//    own line items add to $85,284 (the printed total equals the resident
//    total plus supplemental tuition, while the table lists lower
//    transportation and personal costs for out-of-state students). We show
//    the items and their exact sum, and say so in the note.
//  - College Scorecard school page, id 110671 (net price by family income;
//    the API was rate-limited, so the figures come from the official page).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UC Riverside Common Data Set 2025-2026',
        url: 'https://ir.ucr.edu/sites/default/files/2026-05/cds-2025-2026.pdf',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'UC Riverside Admissions, First Year',
        url: 'https://admissions.ucr.edu/firstyear',
        read: '2026-09-30',
    },
    cost: {
        label: 'UC Riverside Financial Aid, Cost of Attending',
        url: 'https://financialaid.ucr.edu/cost',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110671-University-of-California-Riverside',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-california-riverside',
    kbId: 'university_of_california_riverside',
    name: 'University of California, Riverside',
    shortName: 'UC Riverside',
    location: 'Riverside, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UCR brand site); decoration only.
    branding: {
        primary: '#003DA5',
        secondary: '#FFB81C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_riverside_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 61184,
        admitted: 52676,
        enrolled: 6686,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'California residents', applied: 51828, admitted: 44686 },
            { group: 'Out-of-state', applied: 3137, admitted: 2810 },
            { group: 'International', applied: 6219, admitted: 5180 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UC Riverside\'s Common Data Set reports one first-year acceptance rate for the whole campus, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UC Riverside has no early decision or early action plan. The Common Data Set answers no to both.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application', deadline: 'November 30', decision: 'Notifications begin in March', binding: false },
        ],
        note: 'One UC application covers every UC campus. You can open it on August 1 and submit it between October 1 and November 30. If you are admitted, your Statement of Intent to Register is due May 1.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UC Riverside will not consider SAT or ACT scores when making admission decisions or awarding scholarships, so scores are not considered even if you send them. If you do send them, they may be used another way to meet minimum eligibility requirements, or for course placement after you enroll.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'UC tuition and fees', amounts: [15588, 15588] },
            { item: 'Nonresident supplemental tuition', amounts: [0, 39270] },
            { item: 'Campus and tech fees', amounts: [1202, 1202] },
            { item: 'UC Student Health Insurance', amounts: [2824, 2824] },
            { item: 'Housing (university housing)', amounts: [13793, 13793] },
            { item: 'Food', amounts: [7282, 7282] },
            { item: 'Books and supplies', amounts: [1700, 1700] },
            { item: 'Transportation', amounts: [1275, 1100] },
            { item: 'Personal expenses', amounts: [2875, 2525] },
        ],
        totals: [46539, 85284],
        note: 'UCR\'s estimate for students who start as first-years in 2026-2027 and live in university housing. The out-of-state table prints $85,809 as its total, but its items add to $85,284, the figure shown here. New first-years also pay a one-time $300 orientation fee and a $225 document fee in their first term.',
        source: 'cost',
        netPrice: {
            average: 12672,
            byIncome: [
                { income: '$0 to $30,000', amount: 6871 },
                { income: '$30,001 to $48,000', amount: 7549 },
                { income: '$48,001 to $75,000', amount: 9099 },
                { income: '$75,001 to $110,000', amount: 13190 },
                { income: 'Over $110,000', amount: 30021 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UC Riverside\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
